import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Banknote,
  Upload,
  AlertCircle,
  CreditCard,
  Copy,
  X,
  Loader,
  Zap,
  ExternalLink,
} from "lucide-react";
import "./COBDashboard.css";

// COB v2 API - Qwen VL
const cobExtractV2 = async (file, uid) => {
  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  
  const formData = new FormData();
  formData.append('file', file);
  formData.append('version', 'v2');
  if (uid) formData.append('uid', uid);

  const response = await fetch('/cob/api/extract', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Extraction failed' }));
    throw new Error(error.error || error.message || 'Extraction failed');
  }
  return response.json();
};

const COBExtractV2 = () => {
  const navigate = useNavigate();
  
  // Extract state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uid, setUid] = useState("");
  const [extracting, setExtracting] = useState(false);
  const [extractionResult, setExtractionResult] = useState(null);
  const [extractionError, setExtractionError] = useState(null);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setExtractionResult(null);
      setExtractionError(null);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      setSelectedFile(file);
      setExtractionResult(null);
      setExtractionError(null);
    }
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleExtract = async () => {
    if (!selectedFile) return;

    setExtracting(true);
    setExtractionError(null);
    setExtractionResult(null);

    try {
      const result = await cobExtractV2(selectedFile, uid || undefined);
      setExtractionResult(result);
    } catch (err) {
      console.error("Extraction failed:", err);
      setExtractionError(err.message || "Extraction failed");
    } finally {
      setExtracting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const clearResult = () => {
    setExtractionResult(null);
    setExtractionError(null);
    setSelectedFile(null);
    setUid("");
  };

  const getBankTypeColor = (bankType) => {
    switch (bankType) {
      case "old": return { bg: "#fef3c7", color: "#d97706" };
      case "new": return { bg: "#dcfce7", color: "#16a34a" };
      default: return { bg: "#f1f5f9", color: "#64748b" };
    }
  };

  const getSwaggerUrl = () => {
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:5001/api/extraction/cob/v2/docs';
    }
    return `${window.location.protocol}//${window.location.hostname}/api/extraction/cob/v2/docs`;
  };

  return (
    <div className="cob-dashboard">
      {/* Version Banner */}
      <div className="cob-welcome-banner" style={{ background: 'linear-gradient(135deg, #059669 0%, #047857 100%)' }}>
        <div className="cob-welcome-content">
          <h1>
            <Zap size={28} style={{ marginRight: 10 }} />
            COB v2 - Qwen VL
          </h1>
          <p>Extract cheques using Qwen VL via LiteLLM/Bedrock</p>
        </div>
        <div className="cob-welcome-actions">
          <button 
            className="cob-welcome-btn primary" 
            onClick={() => window.open(getSwaggerUrl(), '_blank')}
            style={{ background: 'rgba(255,255,255,0.2)' }}
          >
            <ExternalLink size={20} />
            API Docs (v2)
          </button>
        </div>
      </div>

      {/* Model Info */}
      <div className="cob-model-info" style={{ 
        background: '#ecfdf5', 
        border: '1px solid #a7f3d0', 
        borderRadius: 8, 
        padding: '12px 16px',
        marginBottom: 20,
        display: 'flex',
        alignItems: 'center',
        gap: 12
      }}>
        <Zap size={20} style={{ color: '#059669' }} />
        <div>
          <strong style={{ color: '#065f46' }}>Model:</strong>{' '}
          <span style={{ color: '#064e3b' }}>qwen3-vl-235b-a22b-bedrock</span>
          <span style={{ marginLeft: 16, color: '#64748b' }}>|</span>
          <span style={{ marginLeft: 16, color: '#475569' }}>Local: LiteLLM Proxy</span>
          <span style={{ marginLeft: 8, color: '#64748b' }}>|</span>
          <span style={{ marginLeft: 8, color: '#475569' }}>Deployed: AWS Bedrock</span>
        </div>
      </div>

      {/* Main Content */}
      <div className="cob-main-grid">
        {/* Left: Upload & Extract */}
        <div className="cob-card">
          <div className="cob-card-header" style={{ borderBottom: '3px solid #059669' }}>
            <Upload size={20} />
            <span>Extract with Qwen VL (v2)</span>
          </div>
          <div className="cob-card-body">
            {/* File Upload Zone */}
            <div 
              className={`cob-upload-zone ${selectedFile ? 'has-file' : ''}`}
              onClick={() => document.getElementById('cob-file-input-v2').click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              style={{ borderColor: selectedFile ? '#059669' : undefined }}
            >
              {selectedFile ? (
                <>
                  <Banknote size={32} className="cob-file-icon" style={{ color: '#059669' }} />
                  <p className="cob-file-name">{selectedFile.name}</p>
                  <p className="cob-file-size">{(selectedFile.size / 1024 / 1024).toFixed(2)} MB</p>
                </>
              ) : (
                <>
                  <Upload size={32} />
                  <p>Click or drag file here to upload</p>
                  <p className="cob-upload-hint">PDF, TIFF, JPG, PNG (max 50MB)</p>
                </>
              )}
            </div>
            <input
              type="file"
              id="cob-file-input-v2"
              accept=".pdf,.tiff,.tif,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            {/* UID Input */}
            <div className="cob-form-group">
              <label htmlFor="cob-uid-v2">UID (optional tracking ID)</label>
              <input
                type="text"
                id="cob-uid-v2"
                value={uid}
                onChange={(e) => setUid(e.target.value)}
                placeholder="Optional unique identifier"
              />
            </div>

            {/* Extract Button */}
            <button
              className="cob-extract-btn"
              onClick={handleExtract}
              disabled={!selectedFile || extracting}
              style={{ background: extracting ? '#94a3b8' : '#059669' }}
            >
              {extracting ? (
                <>
                  <Loader size={18} className="cob-spinner" />
                  Extracting with Qwen VL...
                </>
              ) : (
                <>
                  <Zap size={18} />
                  Extract with Qwen VL (v2)
                </>
              )}
            </button>

            {/* Error Display */}
            {extractionError && (
              <div className="cob-error-message">
                <AlertCircle size={16} />
                <span>{extractionError}</span>
              </div>
            )}
          </div>
        </div>

        {/* Right: Results */}
        <div className="cob-card">
          <div className="cob-card-header" style={{ borderBottom: '3px solid #059669' }}>
            <CreditCard size={20} />
            <span>Extraction Results (Qwen VL)</span>
            {extractionResult && (
              <button className="cob-clear-btn" onClick={clearResult}>
                <X size={16} />
              </button>
            )}
          </div>
          <div className="cob-card-body">
            {extractionResult ? (
              <div className="cob-result-section">
                {/* Result Meta */}
                <div className="cob-result-meta">
                  <div className="cob-meta-item">
                    <span>Version:</span>
                    <span className="cob-status-badge" style={{ background: '#dcfce7', color: '#065f46' }}>
                      v2 - Qwen VL
                    </span>
                  </div>
                  <div className="cob-meta-item">
                    <span>Status:</span>
                    <span className={`cob-status-badge ${extractionResult.success ? 'completed' : 'failed'}`}>
                      {extractionResult.success ? 'completed' : 'failed'}
                    </span>
                  </div>
                  <div className="cob-meta-item">
                    <span>Pages:</span>
                    <strong>{extractionResult.total_pages || 0}</strong>
                  </div>
                  <div className="cob-meta-item">
                    <span>Cheques Found:</span>
                    <strong>{extractionResult.cheques_found || 0}</strong>
                  </div>
                  <div className="cob-meta-item">
                    <span>Processing Time:</span>
                    <strong>{extractionResult.processing_time ? `${extractionResult.processing_time}s` : '-'}</strong>
                  </div>
                  <div className="cob-meta-item">
                    <span>Model:</span>
                    <strong>{extractionResult.model || 'qwen:qwen3-vl-235b-a22b-bedrock'}</strong>
                  </div>
                </div>

                {/* Cheques Display */}
                {extractionResult.cheques && extractionResult.cheques.length > 0 && (
                  <div className="cob-cheques-section">
                    <h4>Extracted Cheques</h4>
                    {extractionResult.cheques.map((cheque, idx) => (
                      <div key={idx} className="cob-cheque-card">
                        <div className="cob-cheque-header">
                          <span>Cheque {idx + 1} (Page {cheque.page_number || '?'})</span>
                          {cheque.bank_type && (
                            <span 
                              className="cob-bank-type"
                              style={getBankTypeColor(cheque.bank_type)}
                            >
                              {cheque.bank_type.toUpperCase()} BANK
                            </span>
                          )}
                        </div>
                        <div className="cob-cheque-fields">
                          <div className="cob-cheque-field">
                            <span className="label">Bank</span>
                            <span className="value">{cheque.bank_name || '-'}</span>
                          </div>
                          <div className="cob-cheque-field">
                            <span className="label">IFSC</span>
                            <span className="value">{cheque.ifsc_code || '-'}</span>
                          </div>
                          <div className="cob-cheque-field">
                            <span className="label">Account No</span>
                            <span className="value">{cheque.account_number || '-'}</span>
                          </div>
                          <div className="cob-cheque-field">
                            <span className="label">MICR</span>
                            <span className="value">{cheque.micr_code || '-'}</span>
                          </div>
                          <div className="cob-cheque-field">
                            <span className="label">Cheque No</span>
                            <span className="value">{cheque.cheque_number || '-'}</span>
                          </div>
                          <div className="cob-cheque-field">
                            <span className="label">Drawer</span>
                            <span className="value">{cheque.drawer_name || '-'}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Form Bank Details */}
                {extractionResult.form_bank_details && (
                  <div className="cob-form-bank-section">
                    <h4>Form Bank Details</h4>
                    {extractionResult.form_bank_details.old_bank && (
                      <div className="cob-bank-detail old">
                        <div className="cob-bank-detail-header">🔴 Old Bank</div>
                        <div className="cob-bank-detail-fields">
                          <div><strong>Bank:</strong> {extractionResult.form_bank_details.old_bank.bank_name || '-'}</div>
                          <div><strong>Account:</strong> {extractionResult.form_bank_details.old_bank.account_number || '-'}</div>
                          <div><strong>IFSC:</strong> {extractionResult.form_bank_details.old_bank.ifsc_code || '-'}</div>
                          <div><strong>MICR:</strong> {extractionResult.form_bank_details.old_bank.micr_code || '-'}</div>
                        </div>
                      </div>
                    )}
                    {extractionResult.form_bank_details.new_bank && (
                      <div className="cob-bank-detail new">
                        <div className="cob-bank-detail-header">🟢 New Bank</div>
                        <div className="cob-bank-detail-fields">
                          <div><strong>Bank:</strong> {extractionResult.form_bank_details.new_bank.bank_name || '-'}</div>
                          <div><strong>Account:</strong> {extractionResult.form_bank_details.new_bank.account_number || '-'}</div>
                          <div><strong>IFSC:</strong> {extractionResult.form_bank_details.new_bank.ifsc_code || '-'}</div>
                          <div><strong>MICR:</strong> {extractionResult.form_bank_details.new_bank.micr_code || '-'}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Copy JSON Button */}
                <div className="cob-result-json-header">
                  <strong>Full Response</strong>
                  <button
                    className="cob-copy-btn"
                    onClick={() => copyToClipboard(JSON.stringify(extractionResult, null, 2))}
                  >
                    <Copy size={14} />
                    Copy JSON
                  </button>
                </div>
              </div>
            ) : (
              <div className="cob-no-results">
                <Zap size={48} style={{ color: '#059669' }} />
                <p>Upload a document and click Extract to use Qwen VL</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default COBExtractV2;
